import { useState, useRef, useEffect } from 'react';
import {
  getSupabase,
  getActiveSession,
  checkSupabaseConnection,
  getUserSubscription,
  forceReconnect,
  setupCloudflareConnection,
  safeRefreshToken
} from '../utils/supabaseClient';
import Link from 'next/link';
import { useRouter } from 'next/router';

export default function ProfileButton() {
  const [user, setUser] = useState(null);
  const [showDropdown, setShowDropdown] = useState(false);
  const [userSubscription, setUserSubscription] = useState(null);
  const [loadingSubscription, setLoadingSubscription] = useState(true);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [connectionStatus, setConnectionStatus] = useState('connected');
  const [mounted, setMounted] = useState(false);
  const [retryCount, setRetryCount] = useState(0);
  const [isFetchingSubscription, setIsFetchingSubscription] = useState(false);
  const [lastSuccessfulFetch, setLastSuccessfulFetch] = useState(0);
  const dropdownRef = useRef(null);
  const lastAttemptTimeRef = useRef(0);
  const MAX_RETRIES = 3;
  const RETRY_DELAY = 2000;
  const router = useRouter();
  
  const lastFetchRef = useRef(0);
  const MIN_FETCH_INTERVAL = 20000;

  const useThrottledEffect = (callback, delay, deps = []) => {
    const [isThrottled, setIsThrottled] = useState(false);
    
    useEffect(() => {
      if (!isThrottled) {
        setIsThrottled(true);
        const timeoutId = setTimeout(() => {
          callback();
          setIsThrottled(false);
        }, delay);
        
        return () => clearTimeout(timeoutId);
      }
    }, [isThrottled, ...deps]);
  };

  useEffect(() => {
    setMounted(true);
    
    const detectPageRefresh = () => {
      if (typeof performance !== 'undefined') {
        const navigation = performance.getEntriesByType("navigation");
        if (navigation.length > 0 && navigation[0].type === "reload") {
          return true;
        }
      }
      
      const now = Date.now();
      const lastVisit = parseInt(localStorage.getItem('penulispro_last_visit') || '0', 10);
      const isRefresh = lastVisit > 0 && now - lastVisit < 3000;
      localStorage.setItem('penulispro_last_visit', now.toString());
      
      return isRefresh;
    };
    
    const isPageRefresh = detectPageRefresh();
    
    const getUser = async () => {
      try {
        const now = Date.now();
        
        if (!isPageRefresh && now - lastFetchRef.current < MIN_FETCH_INTERVAL) {
          console.log('Mencegah permintaan berlebihan, menggunakan data yang sudah ada');
          return;
        }
        
        lastFetchRef.current = now;
        
        if (isPageRefresh) {
          await new Promise(resolve => setTimeout(resolve, 1000));
          
          console.log('Halaman di-refresh, memulihkan koneksi dengan strategi aman...');
          
          if (typeof setupCloudflareConnection === 'function') {
            setupCloudflareConnection();
          } else {
            console.log('setupCloudflareConnection tidak tersedia, menggunakan fallback');
            
            await new Promise(resolve => setTimeout(resolve, 500));
          }
        }
        
        try {
          const session = await getActiveSession();
          
          if (session) {
            setUser(session.user);
            
            setTimeout(async () => {
              try {
                await fetchUserSubscription(session.user.id, isPageRefresh);
              } catch (subError) {
                console.error('Error fetching subscription after session:', subError);
              }
            }, 1000);
          } else if (isPageRefresh) {
            console.log('Tidak ada sesi setelah refresh, mencoba reconnect...');
            try {
              await new Promise(resolve => setTimeout(resolve, 500));
              const reconnected = await forceReconnect();
              
              if (reconnected) {
                console.log('Reconnect berhasil, mencoba get session lagi...');
                
                await new Promise(resolve => setTimeout(resolve, 500));
                
                const newSession = await getActiveSession();
                if (newSession) {
                  setUser(newSession.user);
                  
                  setTimeout(async () => {
                    try {
                      await fetchUserSubscription(newSession.user.id, true);
                    } catch (e) {
                      console.error('Error fetching subscription after reconnect:', e);
                    }
                  }, 2000);
                }
              }
            } catch (reconnectError) {
              console.error('Error reconnecting:', reconnectError);
            }
          }
        } catch (sessionError) {
          console.error('Error getting session:', sessionError);
          
          if (isPageRefresh) {
            await new Promise(resolve => setTimeout(resolve, 2000));
            try {
              await forceReconnect();
            } catch (e) {
              console.error('Error forcing reconnect after session error:', e);
            }
          }
        }
      } catch (error) {
        console.error('Error in getUser:', error);
      }
    };

    if (isPageRefresh) {
      setTimeout(getUser, 500);
    } else {
      getUser();
    }

    const subscribeToEvents = () => {
      const authSubscription = getSupabase().auth.onAuthStateChange(
        async (_event, session) => {
          if (_event === 'SIGNED_OUT') {
            setUser(null);
            setUserSubscription(null);
            return;
          }
          
          if (session?.user) {
            setUser(session.user);
            if (!userSubscription || _event === 'SIGNED_IN') {
              setTimeout(async () => {
                try {
                  await fetchUserSubscription(session.user.id);
                } catch (e) {
                  console.error('Error fetching subscription on auth change:', e);
                }
              }, 800);
            }
          }
        }
      );
      
      return {
        unsubscribe: () => {
          authSubscription.subscription?.unsubscribe();
        }
      };
    };
    
    const timeoutId = setTimeout(() => {
      const subscription = subscribeToEvents();
      
      return () => {
        subscription.unsubscribe();
        clearTimeout(timeoutId);
      };
    }, 1000);
    
    if (typeof window !== 'undefined') {
      const throttledCreditUpdated = throttle(async () => {
        if (user?.id) {
          try {
            await fetchUserSubscription(user.id, true);
          } catch (error) {
            console.error('Error updating subscription data:', error);
          }
        }
      }, 5000);
      
      const throttledVisibilityChange = throttle(async () => {
        if (document.visibilityState === 'visible' && user?.id) {
          try {
            const isConnected = await checkSupabaseConnection();
            setConnectionStatus(isConnected ? 'connected' : 'disconnected');
            
            if (isConnected) {
              await fetchUserSubscription(user.id);
            } else {
              setTimeout(async () => {
                try {
                  const reconnected = await forceReconnect();
                  if (reconnected) {
                    await fetchUserSubscription(user.id, true);
                  }
                } catch (e) {
                  console.error('Error reconnecting on visibility change:', e);
                }
              }, 1000);
            }
          } catch (e) {
            console.error('Error handling visibility change:', e);
          }
        }
      }, 10000);
      
      const handleClickOutside = (event) => {
        if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
          setShowDropdown(false);
        }
      };
      
      const handleShowUpgradeModal = () => {
        setShowUpgradeModal(true);
      };
      
      const handleCreditUpdated = () => {
        throttledCreditUpdated();
      };
      
      const handleVisibilityChange = () => {
        throttledVisibilityChange();
      };
      
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('showUpgradeModal', handleShowUpgradeModal);
      document.addEventListener('creditUpdated', handleCreditUpdated);
      document.addEventListener('visibilitychange', handleVisibilityChange);
      
      const handleOnline = throttle(async () => {
        console.log('Browser kembali online, mencoba reconnect...');
        if (user?.id) {
          try {
            const reconnected = await forceReconnect();
            if (reconnected) {
              setConnectionStatus('connected');
              await fetchUserSubscription(user.id, true);
            }
          } catch (e) {
            console.error('Error pada event online:', e);
          }
        }
      }, 5000);
      
      window.addEventListener('online', handleOnline);
      
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
        document.removeEventListener('showUpgradeModal', handleShowUpgradeModal);
        document.removeEventListener('creditUpdated', handleCreditUpdated);
        document.removeEventListener('visibilitychange', handleVisibilityChange);
        window.removeEventListener('online', handleOnline);
      };
    }
    
    return () => {}; 
  }, [user]);

  function throttle(callback, delay) {
    let lastCall = 0;
    return function(...args) {
      const now = Date.now();
      if (now - lastCall < delay) {
        return;
      }
      lastCall = now;
      return callback(...args);
    };
  }

  const fetchUserSubscription = async (userId, force = false) => {
    if (!userId) {
      console.log('Tidak ada user ID, tidak dapat mengambil data langganan');
      return;
    }
    
    if (isFetchingSubscription) {
      console.log('Sudah ada fetch subscription berjalan, abaikan permintaan baru');
      return;
    }
    
    const now = Date.now();
    if (!force && now - lastAttemptTimeRef.current < MIN_FETCH_INTERVAL) {
      const needsRefresh = !userSubscription || (now - lastSuccessfulFetch > 300000);
      
      if (!needsRefresh) {
        console.log('Mencegah request berlebihan untuk data langganan');
        return;
      }
    }
    
    lastAttemptTimeRef.current = now;
    
    if (!userSubscription) {
      setLoadingSubscription(true);
    }
    
    setIsFetchingSubscription(true);
    
    let retries = 0;
    const MAX_LOCAL_RETRIES = 2;
    const RETRY_DELAY_MS = 1000;
    
    const attemptFetch = async () => {
      try {
        const { data, error, fromCache } = await getUserSubscription(userId, force);
        
        if (error) {
          console.error('Error getting user subscription:', error);
          
          if (!fromCache) {
            setConnectionStatus('disconnected');
            
            if (retries < MAX_LOCAL_RETRIES) {
              retries++;
              console.log(`Mencoba mengambil subscription ulang (${retries}/${MAX_LOCAL_RETRIES})...`);
              
              await new Promise(resolve => setTimeout(resolve, RETRY_DELAY_MS * retries));
              return attemptFetch();
            }
            
            if (retryCount < MAX_RETRIES) {
              setRetryCount(prev => prev + 1);
              
              setTimeout(async () => {
                try {
                  console.log('Mencoba force reconnect...');
                  const reconnected = await forceReconnect();
                  
                  if (reconnected) {
                    console.log('Berhasil reconnect, mencoba ambil data langganan lagi');
                    setTimeout(async () => {
                      try {
                        await fetchUserSubscription(userId, true);
                        setRetryCount(0);
                      } catch (e) {
                        console.error('Error setelah reconnect:', e);
                      } finally {
                        setLoadingSubscription(false);
                        setIsFetchingSubscription(false);
                      }
                    }, 1000);
                    
                    return;
                  }
                } catch (e) {
                  console.error('Error melakukan reconnect:', e);
                }
                
                setLoadingSubscription(false);
                setIsFetchingSubscription(false);
              }, RETRY_DELAY * retryCount);
              
              return;
            }
          }
        } else if (data) {
          const isDataChanged = !userSubscription || 
                               userSubscription.credits_remaining !== data.credits_remaining ||
                               userSubscription.plan_name !== data.plan_name ||
                               userSubscription.is_active !== data.is_active;
                               
          if (isDataChanged) {
            console.log('Data langganan diperbarui:', data.plan_name, 'dengan', data.credits_remaining, 'kredit tersisa');
            setUserSubscription(data);
            setLastSuccessfulFetch(now);
          } else {
            console.log('Data langganan tidak berubah, skip update state');
          }
          
          setConnectionStatus('connected');
          setRetryCount(0);
        } else {
          console.log('Tidak ada data langganan yang aktif');
          setUserSubscription(null);
          setRetryCount(0);
        }
      } catch (error) {
        console.error('Error retrieving subscription:', error);
        setConnectionStatus('disconnected');
        
        if (retries < MAX_LOCAL_RETRIES) {
          retries++;
          console.log(`Mencoba mengambil subscription ulang setelah error (${retries}/${MAX_LOCAL_RETRIES})...`);
          
          await new Promise(resolve => setTimeout(resolve, RETRY_DELAY_MS * retries));
          return attemptFetch();
        }
        
        if (retryCount < MAX_RETRIES) {
          setRetryCount(prev => prev + 1);
          
          setTimeout(async () => {
            try {
              await forceReconnect();
              await fetchUserSubscription(userId, true);
            } catch (e) {
              console.error('Error pada auto reconnect:', e);
            } finally {
              setLoadingSubscription(false);
              setIsFetchingSubscription(false);
            }
          }, RETRY_DELAY * (retryCount + 1));
          
          return;
        }
      }
      
      setLoadingSubscription(false);
      setIsFetchingSubscription(false);
    };
    
    await attemptFetch();
  };

  const toggleDropdown = () => {
    setShowDropdown(!showDropdown);
    
    if (!showDropdown) {
      const now = Date.now();
      if (user?.id && !isFetchingSubscription && 
          ((now - lastSuccessfulFetch > 300000) || 
           (connectionStatus === 'disconnected') || 
           (!userSubscription))) {
        
        console.log('Memperbarui data langganan saat membuka dropdown');
        lastFetchRef.current = now;
        fetchUserSubscription(user.id);
      }
    }
  };

  const handleSignOut = async () => {
    try {
      await getSupabase().auth.signOut();
      window.location.href = '/login?from=logout';
    } catch (error) {
      console.error('Error signing out:', error);
      alert('Gagal keluar. Silakan coba lagi.');
    }
  };

  const openUpgradeModal = () => {
    setShowDropdown(false);
    setShowUpgradeModal(true);
  };

  const handleUpgrade = () => {
    setShowUpgradeModal(false);
    router.push('/subscription');
  };

  const handleRetryLoadSubscription = async () => {
    if (user?.id) {
      setLoadingSubscription(true);
      
      try {
        console.log('Memulai paksa reconnect dari tombol coba lagi...');
        const reconnected = await forceReconnect();
        setConnectionStatus(reconnected ? 'connected' : 'disconnected');
        
        if (reconnected) {
          console.log('Reconnect berhasil, mengambil data langganan...');
          await fetchUserSubscription(user.id, true);
          setRetryCount(0);
        } else {
          setLoadingSubscription(false);
          console.log('Reconnect gagal, mencoba strategi alternatif dengan safeRefreshToken...');
          
          try {
            const { data, error } = await safeRefreshToken();
            if (!error || (error && error.isThrottled)) {
              console.log('Token di-refresh dengan aman, mencoba ambil data langganan lagi');
              await fetchUserSubscription(user.id, true);
            } else {
              console.error('Gagal refresh token:', error?.message);
            }
          } catch (e) {
            console.error('Gagal refresh token:', e);
          }
        }
      } catch (error) {
        console.error('Error reconnecting:', error);
        setLoadingSubscription(false);
      }
    }
  };

  const handleNavigation = (e, path) => {
    e.preventDefault();
    
    sessionStorage.setItem('nextRoute', path);
    
    if (user) {
      sessionStorage.setItem('preserveUser', JSON.stringify({
        id: user.id,
        timestamp: Date.now()
      }));
    }
    
    window.location.href = path + (path.includes('?') ? '&' : '?') + '_refresh=' + Date.now();
  };

  if (!mounted || !user) return null;

  return (
    <div className="profile-container" ref={dropdownRef} data-testid="profile-container" style={{ position: 'relative' }}>
      <button 
        className="profile-button mobile-profile-btn" 
        onClick={toggleDropdown} 
        aria-label="Menu Profil Pengguna"
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          position: 'relative',
          background: 'transparent',
          border: 'none',
          cursor: 'pointer',
          padding: 0
        }}
      >
        <img 
          src={user.user_metadata?.avatar_url || 'https://via.placeholder.com/40'} 
          alt="Profile" 
          className="profile-image mobile-profile-img"
          style={{
            width: '40px',
            height: '40px',
            borderRadius: '50%',
            objectFit: 'cover',
            border: '2px solid #ffa000'
          }}
        />
        {userSubscription && (
          <div 
            className="profile-credit-badge" 
            data-testid="profile-credit-badge"
            style={{
              position: 'absolute',
              top: '-5px',
              right: '-5px',
              backgroundColor: '#ffa000',
              color: '#000',
              fontWeight: 'bold',
              fontSize: '12px',
              minWidth: '20px',
              height: '20px',
              borderRadius: '10px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: '0 4px',
              boxShadow: '0 2px 4px rgba(0,0,0,0.2)'
            }}
          >
            {userSubscription.credits_remaining}
          </div>
        )}
      </button>
      
      <div 
        className={`profile-dropdown ${showDropdown ? 'active' : ''}`} 
        style={{ 
        zIndex: 1000, 
        position: 'absolute',
        right: 0,
          top: 'calc(100% + 8px)',
          width: '280px',
          backgroundColor: '#222',
          borderRadius: '12px',
          boxShadow: '0 4px 20px rgba(0,0,0,0.3)',
          overflow: 'hidden',
          opacity: showDropdown ? 1 : 0,
          transform: showDropdown ? 'translateY(0)' : 'translateY(-10px)',
          visibility: showDropdown ? 'visible' : 'hidden',
          transition: 'all 0.3s ease',
          border: '1px solid rgba(255,255,255,0.08)'
        }}
      >
        <div 
          className="profile-header"
          style={{
            padding: '16px',
            borderBottom: '1px solid #333',
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
            background: 'linear-gradient(135deg, #2a2a2a, #222222)'
          }}
        >
          <img 
            src={user.user_metadata?.avatar_url || 'https://via.placeholder.com/40'} 
            alt="Profile"
            style={{
              width: '50px',
              height: '50px',
              borderRadius: '50%',
              objectFit: 'cover',
              border: '2px solid #ffa000'
            }}
          />
          <div style={{ flex: 1 }}>
            <div style={{ fontWeight: 600, fontSize: '14px', color: '#fff' }}>
              {user.user_metadata?.full_name || user.email}
            </div>
            <div style={{ fontSize: '12px', color: '#aaa', marginTop: '2px' }}>
              {user.email}
            </div>
          </div>
        </div>
        
        <div 
          className="profile-subscription-info"
          style={{
            padding: '16px',
            borderBottom: '1px solid #333',
            background: '#1a1a1a'
          }}
        >
          {!loadingSubscription && userSubscription ? (
            <>
              <div className="profile-subscription-details">
                <div 
                  className="profile-subscription-name"
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    fontSize: '14px',
                    fontWeight: 600,
                    color: '#eee',
                    marginBottom: '8px'
                  }}
                >
                  <span style={{ 
                    backgroundColor: '#ffa000',
                    padding: '3px 8px',
                    borderRadius: '4px',
                    color: '#000',
                    fontSize: '12px'
                  }}>
                  Paket {userSubscription.plan_name}
                  </span>
                </div>
                <div 
                  className="profile-subscription-credits"
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px',
                    fontSize: '13px',
                    color: '#bbb',
                    marginBottom: '4px'
                  }}
                >
                  <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="12" cy="12" r="10"></circle>
                    <line x1="12" y1="8" x2="12" y2="12"></line>
                    <line x1="12" y1="16" x2="12.01" y2="16"></line>
                  </svg>
                  <span 
                    className="profile-credit-count"
                    style={{
                      fontWeight: 'bold',
                      color: '#ffa000'
                    }}
                  >
                    {userSubscription.credits_remaining}
                  </span> 
                  kredit tersisa
                </div>
                <div 
                  className="profile-subscription-expiry"
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px',
                    fontSize: '13px',
                    color: '#bbb'
                  }}
                >
                  <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect>
                    <line x1="16" y1="2" x2="16" y2="6"></line>
                    <line x1="8" y1="2" x2="8" y2="6"></line>
                    <line x1="3" y1="10" x2="21" y2="10"></line>
                  </svg>
                  Berlaku hingga: {new Date(userSubscription.subscription_end).toLocaleDateString('id-ID')}
                </div>
              </div>
              <Link 
                href="/subscription" 
                className="profile-upgrade-button"
                style={{
                  display: 'block',
                  marginTop: '12px',
                  padding: '8px 12px',
                  backgroundColor: '#ffa000',
                  color: '#000',
                  textAlign: 'center',
                  borderRadius: '6px',
                  fontWeight: 500,
                  fontSize: '13px',
                  textDecoration: 'none',
                  transition: 'background-color 0.2s ease',
                  border: 'none',
                  cursor: 'pointer'
                }}
              >
                Upgrade Paket
              </Link>
            </>
          ) : loadingSubscription ? (
            <div 
              className="loading-subscription"
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '10px',
                padding: '10px 0'
              }}
            >
              <div 
                className="loading-spinner"
                style={{
                  width: '16px',
                  height: '16px',
                  border: '2px solid rgba(255,255,255,0.1)',
                  borderLeftColor: '#ffa000',
                  borderRadius: '50%',
                  animation: 'spin 1s linear infinite'
                }}
              ></div>
              <span style={{ fontSize: '13px', color: '#bbb' }}>Memuat data paket...</span>
            </div>
          ) : connectionStatus === 'disconnected' ? (
            <>
              <div 
                className="profile-connection-error"
                style={{
                  fontSize: '13px',
                  color: '#ffb8b8',
                  marginBottom: '12px',
                  padding: '8px',
                  backgroundColor: 'rgba(255, 80, 80, 0.1)',
                  borderRadius: '4px',
                  textAlign: 'center'
                }}
              >
                Gagal terhubung ke server. Silakan periksa koneksi internet Anda.
              </div>
              <button 
                onClick={handleRetryLoadSubscription}
                className="profile-retry-button"
                style={{
                  display: 'block',
                  width: '100%',
                  marginTop: '8px',
                  padding: '8px 12px',
                  backgroundColor: '#555',
                  color: '#fff',
                  textAlign: 'center',
                  borderRadius: '6px',
                  fontWeight: 500,
                  fontSize: '13px',
                  border: 'none',
                  cursor: 'pointer',
                  transition: 'background-color 0.2s ease'
                }}
              >
                Coba Lagi
              </button>
            </>
          ) : (
            <>
              <div 
                className="profile-no-subscription"
                style={{
                  fontSize: '13px',
                  color: '#bbb',
                  marginBottom: '12px',
                  padding: '8px',
                  backgroundColor: 'rgba(255, 160, 0, 0.1)',
                  borderRadius: '4px',
                  textAlign: 'center'
                }}
              >
                Anda belum memiliki paket berlangganan.
              </div>
              <Link 
                href="/subscription" 
                className="profile-upgrade-button"
                style={{
                  display: 'block',
                  marginTop: '12px',
                  padding: '8px 12px',
                  backgroundColor: '#ffa000',
                  color: '#000',
                  textAlign: 'center',
                  borderRadius: '6px',
                  fontWeight: 500,
                  fontSize: '13px',
                  textDecoration: 'none',
                  transition: 'background-color 0.2s ease',
                  border: 'none',
                  cursor: 'pointer'
                }}
              >
                Dapatkan Paket
              </Link>
            </>
          )}
        </div>
        
        <div 
          className="profile-menu-links"
          style={{
            padding: '16px'
          }}
        >
          <div 
            className="profile-menu-nav"
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              marginBottom: '12px'
            }}
          >
            <Link 
              href="/" 
              className="profile-menu-item"
              style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: '6px',
                padding: '10px',
                color: '#bbb',
                textDecoration: 'none',
                fontSize: '12px',
                transition: 'all 0.2s ease',
                backgroundColor: '#2a2a2a',
                borderRadius: '8px',
                width: '48%'
              }}
              onClick={(e) => {
                e.preventDefault();
                handleNavigation(e, '/');
              }}
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ color: '#ffa000' }}>
                <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"></path>
                <polyline points="9 22 9 12 15 12 15 22"></polyline>
              </svg>
              <span>Beranda</span>
            </Link>
            
            <Link 
              href="/articles" 
              className="profile-menu-item"
              style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: '6px',
                padding: '10px',
                color: '#bbb',
                textDecoration: 'none',
                fontSize: '12px',
                transition: 'all 0.2s ease',
                backgroundColor: '#2a2a2a',
                borderRadius: '8px',
                width: '48%'
              }}
              onClick={(e) => {
                e.preventDefault();
                handleNavigation(e, '/articles');
              }}
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ color: '#ffa000' }}>
                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
                <polyline points="14 2 14 8 20 8"></polyline>
                <line x1="16" y1="13" x2="8" y2="13"></line>
                <line x1="16" y1="17" x2="8" y2="17"></line>
                <polyline points="10 9 9 9 8 9"></polyline>
              </svg>
              <span>Artikel Saya</span>
            </Link>
          </div>
          
          <button 
            onClick={handleSignOut} 
            className="profile-menu-signout"
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '8px',
              width: '100%',
              padding: '10px',
              color: '#fff',
              backgroundColor: '#d32f2f',
              border: 'none',
              borderRadius: '8px',
              fontSize: '13px',
              fontWeight: 500,
              cursor: 'pointer',
              transition: 'background-color 0.2s ease'
            }}
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ color: '#fff' }}>
            <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"></path>
            <polyline points="16 17 21 12 16 7"></polyline>
            <line x1="21" y1="12" x2="9" y2="12"></line>
          </svg>
          <span>Keluar</span>
          </button>
        </div>
      </div>

      {showUpgradeModal && (
        <div 
          className="upgrade-modal-backdrop" 
          onClick={() => setShowUpgradeModal(false)}
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0,0,0,0.7)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 9999,
            backdropFilter: 'blur(3px)'
          }}
        >
          <div 
            className="upgrade-modal-content" 
            onClick={e => e.stopPropagation()}
            style={{
              width: '90%',
              maxWidth: '400px',
              backgroundColor: '#222',
              borderRadius: '12px',
              padding: '24px',
              boxShadow: '0 10px 30px rgba(0,0,0,0.3)',
              animation: 'fadeInUp 0.3s ease',
              border: '1px solid rgba(255,255,255,0.1)'
            }}
          >
            <h3 style={{
              margin: '0 0 16px 0',
              fontSize: '18px',
              fontWeight: 600,
              color: '#fff',
              textAlign: 'center'
            }}>Upgrade Paket Anda</h3>
            <p style={{
              margin: '0 0 24px 0',
              fontSize: '14px',
              color: '#bbb',
              textAlign: 'center',
              lineHeight: 1.5
            }}>Dapatkan lebih banyak kredit untuk menghasilkan artikel berkualitas!</p>
            <div 
              className="upgrade-modal-actions"
              style={{
                display: 'flex',
                gap: '12px',
                justifyContent: 'center'
              }}
            >
              <button 
                className="upgrade-confirm-btn" 
                onClick={handleUpgrade}
                style={{
                  padding: '10px 20px',
                  backgroundColor: '#ffa000',
                  color: '#000',
                  border: 'none',
                  borderRadius: '6px',
                  fontWeight: 500,
                  cursor: 'pointer',
                  transition: 'background-color 0.2s ease',
                  flex: 1
                }}
              >
                Lihat Paket
              </button>
              <button 
                className="upgrade-cancel-btn" 
                onClick={() => setShowUpgradeModal(false)}
                style={{
                  padding: '10px 20px',
                  backgroundColor: 'rgba(255,255,255,0.05)',
                  color: '#ddd',
                  border: '1px solid rgba(255,255,255,0.1)',
                  borderRadius: '6px',
                  fontWeight: 500,
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                  flex: 1
                }}
              >
                Nanti Saja
              </button>
            </div>
          </div>
        </div>
      )}
      
      <style jsx global>{`
        @keyframes fadeInUp {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
        
        .profile-menu-item:hover {
          background-color: #333 !important;
          color: #fff !important;
        }
        
        .profile-upgrade-button:hover {
          background-color: #f09000 !important;
        }
        
        .profile-retry-button:hover {
          background-color: #666 !important;
        }
        
        .upgrade-confirm-btn:hover {
          background-color: #f09000 !important;
        }
        
        .upgrade-cancel-btn:hover {
          background-color: rgba(255,255,255,0.1) !important;
        }
        
        .profile-menu-signout:hover {
          background-color: #b71c1c !important;
        }
      `}</style>
    </div>
  );
}